/*****************************************************
Naam: DashboardModals.jsx
Locatie: src/components/Dashboard/DashboardModals.jsx
Datum: 2026-02-07
Versie: 1.0.0
******************************************************
Beschrijving:
Alle modal rendering voor het Dashboard.
******************************************************/

import React, { Suspense, lazy } from 'react';
import ErrorBoundary from '../ErrorBoundary';
import TrackingScreen from '../TrackingScreen';
import BerichtenModal from '../BerichtenModal';

// Lazy loaded modals
const SettingsModal = lazy(() => import('../SettingsModal'));
const KlantenModal = lazy(() => import('../KlantenModal'));
const VasteZendingenBeheer = lazy(() => import('../VasteZendingenBeheer'));
const VervoerdersModal = lazy(() => import('../VervoerdersModal'));
const AanvraagInkoopModal = lazy(() => import('../AanvraagInkoopModal'));
const PrijsopgaveModal = lazy(() => import('../PrijsopgaveModal'));
const OpdrachtbevestigingModal = lazy(() => import('../OpdrachtbevestigingModal'));
const StatusAanvragenModal = lazy(() => import('../StatusAanvragenModal'));
const EmailQueueModal = lazy(() => import('../EmailQueueModal'));
const EmailAanvraagPreviewModal = lazy(() => import('../EmailAanvraagPreviewModal'));
const LijstenModal = lazy(() => import('../LijstenModal'));
const DashboardNotificationsModal = lazy(() => import('../DashboardNotificationsModal'));
const EmailCheckModal = lazy(() => import('../EmailCheckModal'));
const FacturenModal = lazy(() => import('../FacturenModal'));
const AfgeschrevenModal = lazy(() => import('../AfgeschrevenModal'));

// Loading fallback
const ModalLoader = () => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 shadow-xl">
      <div className="flex items-center gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        <span className="text-gray-700 font-medium">Laden...</span>
      </div>
    </div>
  </div>
);

// Modal wrapper
const ModalWrapper = ({ children }) => (
  <ErrorBoundary>
    <Suspense fallback={<ModalLoader />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

const DashboardModals = ({
  activeModal,
  selectedAanvraagID,
  emailData,
  emailQueue,
  showPreview,
  previewEmail,
  showTracking,
  onModalClose,
  onModalCloseWithCleanup,
  onModalOpen,
  onLogoUpdate,
  onBackgroundUpdate,
  onLoadNotificationCount,
  refreshDashboardCounts,
  onCreateAanvraagFromQueue,
  onDeleteEmailFromQueue,
  onPreviewEmail,
  onImportFromPreview,
  onClosePreview,
  onCloseTracking,
  showBerichten,
  onCloseBerichten,
}) => {
  return (
    <>
      {/* Settings Modal */}
      {activeModal === 'instellingen' && (
        <ModalWrapper>
          <SettingsModal 
            onClose={onModalClose} 
            onLogoUpdate={onLogoUpdate}
            onBackgroundUpdate={onBackgroundUpdate}
          />
        </ModalWrapper>
      )}

      {/* Notifications Modal */}
      {activeModal === 'notifications' && (
        <ModalWrapper>
          <DashboardNotificationsModal 
            show={true}
            onClose={() => {
              onModalClose();
              onLoadNotificationCount();
            }}
          />
        </ModalWrapper>
      )}

      {/* Email Check Modal */}
      {activeModal === 'emailCheck' && (
        <ModalWrapper>
          <EmailCheckModal 
            show={true}
            onClose={onModalClose}
          />
        </ModalWrapper>
      )}

      {/* Email Queue Modal */}
      {activeModal === 'emailQueue' && (
        <ModalWrapper>
          <EmailQueueModal
            isOpen={true}
            onClose={onModalClose}
            emailQueue={emailQueue}
            onCreateAanvraag={onCreateAanvraagFromQueue}
            onDeleteEmail={onDeleteEmailFromQueue}
            onPreview={onPreviewEmail}
          />
        </ModalWrapper>
      )}

      {/* Email Preview Modal */}
      <EmailAanvraagPreviewModal
        isOpen={showPreview}
        onClose={onClosePreview}
        emailData={previewEmail}
        onImport={onImportFromPreview}
      />

      {/* Klanten Modal */}
      {activeModal === 'klanten' && (
        <ModalWrapper>
          <KlantenModal onClose={onModalClose} onDataChange={refreshDashboardCounts} />
        </ModalWrapper>
      )}

      {/* Vaste Zendingen Modal */}
      {activeModal === 'vaste_zendingen' && (
        <ModalWrapper>
          <div style={{ background:'#fff', borderRadius:12, padding:28, width:'min(95vw,800px)', maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'#1a364c' }}>📦 Vaste Zendingen — Alle Klanten</h2>
              <button onClick={onModalClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#64748b' }}>✕</button>
            </div>
            <VasteZendingenBeheer klantId={null} isKlantPortaal={false} />
          </div>
        </ModalWrapper>
      )}

      {/* Vervoerders Modal */}
      {activeModal === 'vervoerders' && (
        <ModalWrapper>
          <VervoerdersModal onClose={onModalClose} onDataChange={refreshDashboardCounts} />
        </ModalWrapper>
      )}

      {/* Aanvraag Inkoop Modal */}
      {activeModal === 'aanvraag' && (
        <ModalWrapper>
          <AanvraagInkoopModal 
            onClose={onModalCloseWithCleanup}
            initialAanvraagID={selectedAanvraagID}
            emailData={emailData}
            onDataChange={refreshDashboardCounts}
          />
        </ModalWrapper>
      )}

      {/* Prijsopgave Modal */}
      {activeModal === 'prijsopgave' && (
        <ModalWrapper>
          <PrijsopgaveModal 
            onClose={onModalCloseWithCleanup}
            initialAanvraagID={selectedAanvraagID}
            onDataChange={refreshDashboardCounts}
          />
        </ModalWrapper>
      )}

      {/* Opdrachtbevestiging Modal */}
      {activeModal === 'opdrachtbevestiging' && (
        <ModalWrapper>
          <OpdrachtbevestigingModal 
            onClose={onModalCloseWithCleanup}
            initialAanvraagID={selectedAanvraagID}
            onDataChange={refreshDashboardCounts}
          />
        </ModalWrapper>
      )}

      {/* Geoffreerd Modal (StatusAanvragen gefilterd op "Prijs geoffreerd") */}
      {activeModal === 'geoffreerd' && (
        <ModalWrapper>
          <StatusAanvragenModal 
            onClose={onModalClose} 
            onOpenModal={onModalOpen}
            defaultFilter="Prijs geoffreerd"
          />
        </ModalWrapper>
      )}

      {/* Status Aanvragen Modal */}
      {activeModal === 'status' && (
        <ModalWrapper>
          <StatusAanvragenModal 
            onClose={onModalClose} 
            onOpenModal={onModalOpen}
          />
        </ModalWrapper>
      )}
      
      {/* Pallet Tracking scherm */}
      {showTracking && (
        <TrackingScreen onClose={onCloseTracking} />
      )}



      {/* Lijsten Modal */}
      {activeModal === 'lijsten' && (
        <ModalWrapper>
          <LijstenModal 
            isOpen={true}
            onClose={onModalClose}
          />
        </ModalWrapper>
      )}

      {/* Facturen Modal */}
      {activeModal === 'facturen' && (
        <ModalWrapper>
          <FacturenModal 
            show={true}
            onClose={onModalClose}
          />
        </ModalWrapper>
      )}

      {/* Afgeschreven Modal */}
      {activeModal === 'afgeschreven' && (
        <ModalWrapper>
          <AfgeschrevenModal 
            onClose={onModalClose}
            onDataChange={refreshDashboardCounts}
          />
        </ModalWrapper>
      )}
      {/* Berichten Modal */}
      {showBerichten && (
        <BerichtenModal onClose={onCloseBerichten} />
      )}
    </>
  );
};

export default DashboardModals;
